import { Texture, Program, Mesh, Plane, Transform } from "ogl";

import { map, ringAngle } from "../utils/math";
import { fragment, vertex } from "../../shaders";
import {
  CARD_ASPECT,
  CARD_BORDER_RADIUS,
  CARD_COUNT,
  GRID_X,
  GRID_Y,
  IMAGES,
} from "../utils/config";

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export class CardField {
  constructor(stage) {
    this.stage = stage;
    this.cards = [];
    this.radius = 1.72;
    this.planeW = 0.28;
    this.planeH = this.planeW / CARD_ASPECT;
    this.geometry = new Plane(stage.gl, {
      width: 1,
      height: 1,
      widthSegments: 1,
      heightSegments: 1,
    });
  }

  layoutMetrics() {
    const shortest = Math.min(this.stage.viewWidth, this.stage.viewHeight);
    this.radius = shortest * 0.29;
    this.planeW = shortest * 0.046;
    this.planeH = this.planeW / CARD_ASPECT;
  }

  gridPos(index) {
    const spanX = this.stage.viewWidth * 0.78;
    const spanY = this.stage.viewHeight * 0.62;
    return {
      x: map(GRID_X[index], 0, CARD_COUNT - 1, -spanX / 2, spanX / 2),
      y: map(GRID_Y[index], 0, 7, -spanY / 2, spanY / 2),
    };
  }

  rowPos(index) {
    const span = this.stage.viewWidth * 0.82;
    return {
      x: map(index, 0, CARD_COUNT - 1, -span / 2, span / 2),
      y: 0,
    };
  }

  ringPos(index, radius = this.radius) {
    const a = ringAngle(index);
    return {
      x: Math.cos(a) * radius,
      y: Math.sin(a) * radius,
      rotZ: a + Math.PI * 0.5,
    };
  }

  async create() {
    this.layoutMetrics();
    const images = await Promise.all(IMAGES.map(loadImage));

    images.forEach((img, index) => {
      const texture = new Texture(this.stage.gl, {
        generateMipmaps: true,
        minFilter: this.stage.gl.LINEAR_MIPMAP_LINEAR,
        magFilter: this.stage.gl.LINEAR,
      });
      texture.image = img;

      const pxPerUnit = this.stage.height / this.stage.viewHeight;
      const program = new Program(this.stage.gl, {
        vertex,
        fragment,
        uniforms: {
          tMap: { value: texture },
          uImageSize: {
            value: [
              img.naturalWidth || img.width,
              img.naturalHeight || img.height,
            ],
          },
          uPlaneSize: { value: [this.planeW, this.planeH] },
          uPlanePixels: {
            value: [this.planeW * pxPerUnit, this.planeH * pxPerUnit],
          },
          uBorderRadius: { value: CARD_BORDER_RADIUS },
          uOpacity: { value: 0 },
        },
        transparent: true,
        cullFace: false,
      });

      const mesh = new Mesh(this.stage.gl, {
        geometry: this.geometry,
        program,
      });
      mesh.scale.set(this.planeW, this.planeH, 1);

      const flip = new Transform();
      mesh.setParent(flip);

      const pivot = new Transform();
      flip.setParent(pivot);
      pivot.setParent(this.stage.ring);

      const start = this.gridPos(index);
      pivot.position.set(start.x, start.y, 0);

      this.cards.push({
        index,
        img,
        texture,
        program,
        mesh,
        flip,
        pivot,
        angle: ringAngle(index),
        anim: {
          x: start.x,
          y: start.y,
          z: 0,
          rotX: 0,
          rotY: 0,
          rotZ: 0,
          opacity: 0,
          extraR: 0,
        },
        hover: {
          extraR: 0,
          rotY: 0,
          z: 0,
        },
      });
    });

    return this.cards;
  }

  apply(card) {
    const { anim, hover, pivot, flip, program, mesh } = card;
    const radiusBoost = hover.extraR;
    pivot.position.set(anim.x, anim.y, anim.z + hover.z);
    if (radiusBoost && (anim.x !== 0 || anim.y !== 0)) {
      const len = Math.hypot(anim.x, anim.y) || 1;
      const scale = (len + radiusBoost) / len;
      pivot.position.x = anim.x * scale;
      pivot.position.y = anim.y * scale;
    }
    pivot.rotation.set(anim.rotX, 0, anim.rotZ);
    flip.rotation.set(0, hover.rotY + anim.rotY, 0);
    program.uniforms.uOpacity.value = anim.opacity;
    program.uniforms.uPlaneSize.value = [this.planeW, this.planeH];
    const pxPerUnit = this.stage.height / this.stage.viewHeight;
    program.uniforms.uPlanePixels.value = [
      this.planeW * pxPerUnit,
      this.planeH * pxPerUnit,
    ];
    mesh.scale.set(this.planeW, this.planeH, 1);
  }

  applyAll() {
    this.cards.forEach((card) => this.apply(card));
  }

  resize() {
    this.layoutMetrics();
    this.applyAll();
  }
}
